export interface ExerciseSubmission {
  id: string;
  type: 'text' | 'link' | 'file';
  content: string;
  createdAt: number;
}

export interface AIBeginnersExercise {
  id: string;
  number: number;
  title: string;
  instructions: string;
  artifact: string | null;
  artifactRequired: boolean;
  uploadUrl?: string; // Legacy
  notes?: string;     // Legacy
  submissions?: ExerciseSubmission[];
  completed?: boolean;
}

export interface AIBeginnersSession {
  number: number;
  slug: string;
  title: string;
  subtitle: string;
  outcome: string;
  exercises: AIBeginnersExercise[];
}

export interface AIBeginnersContent {
  sessions: AIBeginnersSession[];
}

export const AI_BEGINNERS_SEED: AIBeginnersContent = {
  sessions: [
    {
      number: 1,
      slug: "first-ai-conversation",
      title: "First AI Conversation",
      subtitle: "Log in, have a real AI conversation, and share what you learned.",
      outcome: "Log in, have a real AI conversation using *situation + question + format*, and share what you learned.",
      exercises: [
        {
          id: "s1e1",
          number: 1,
          title: "Getting Logged In",
          instructions: `**Goal of this exercise**\nTo successfully access your chosen AI tool and locate the interface.\n\n**What you are doing**\nYou will navigate to an AI platform (like ChatGPT, Claude, or Gemini), sign up or log in using your email, and locate the chat text box to prepare for your first prompt.`,
          artifact: "Screenshot of the logged-in chat window.",
          artifactRequired: true,
        },
        {
          id: "s1e2",
          number: 2,
          title: "Your First Conversation",
          instructions: `**Goal of this exercise**\nTo practice writing a complete and foundational AI prompt.\n\n**What you are doing**\nYou will ask the AI a question about a topic you actually care about using the formula: Situation + Question + Format. You will also practice using a "power move" like asking the AI to make the response shorter or put it into a list.`,
          artifact: "Screenshot or copy/paste of the full conversation.",
          artifactRequired: true,
        },
        {
          id: "s1e3",
          number: 3,
          title: "Share + Wrap Up",
          instructions: `**Goal of this exercise**\nTo reflect on your first AI response and verify its accuracy.\n\n**What you are doing**\nYou will review the AI's answer, answer three quick discussion questions about how the interaction felt, and think about how you could check the AI's claims from a trusted source.`,
          artifact: "Optional reflection note.",
          artifactRequired: false,
        },
      ],
    },
    {
      number: 2,
      slug: "writing-with-ai",
      title: "Writing with AI",
      subtitle: "Build a RACE prompt, use it to draft a real document, and edit it with a 4-item checklist.",
      outcome: "Build a RACE prompt, use it to draft a real document, and edit it with a 4-item checklist.",
      exercises: [
        {
          id: "s2e1",
          number: 1,
          title: "Build a RACE Prompt",
          instructions: `**Goal of this exercise**\nTo structure a professional document generation request using a framework.\n\n**What you are doing**\nYou will build a prompt using the RACE framework (Role, Action, Context, Expectations) to draft a business email, difficult message, or complaint.`,
          artifact: "Your full assembled RACE prompt (text).",
          artifactRequired: true,
        },
        {
          id: "s2e2",
          number: 2,
          title: "Write Your Thing",
          instructions: `**Goal of this exercise**\nTo generate a functional first draft from your RACE prompt.\n\n**What you are doing**\nYou will paste your structured RACE prompt into the AI, review the output, and guide the AI to tweak the tone or formatting if it isn't quite right.`,
          artifact: "Your first AI-generated draft.",
          artifactRequired: true,
        },
        {
          id: "s2e3",
          number: 3,
          title: "Make It Yours",
          instructions: `**Goal of this exercise**\nTo manually edit the AI's output so it aligns with your authentic voice.\n\n**What you are doing**\nYou will put the draft through a 4-item editing checklist to ensure accuracy and tone, making any final adjustments so you would be comfortable sending the document under your own name.`,
          artifact: "Final edited draft + your completed 4-item checklist.",
          artifactRequired: true,
        },
      ],
    },
    {
      number: 3,
      slug: "asking-better-questions",
      title: "Asking Better Questions",
      subtitle: "Turn a vague question into a specific one, get a real answer, and verify it.",
      outcome: "Turn a vague question into a specific one, get a real answer, and verify it.",
      exercises: [
        {
          id: "s3e1",
          number: 1,
          title: "From Vague to Specific",
          instructions: `**Goal of this exercise**\nTo understand how adding context changes the quality of AI answers.\n\n**What you are doing**\nYou will review three different examples comparing a generic question against a highly specific situational question, noting what details made the output more useful.`,
          artifact: null,
          artifactRequired: false,
        },
        {
          id: "s3e2",
          number: 2,
          title: "Ask Your Real Question",
          instructions: `**Goal of this exercise**\nTo solve a real problem or confusion by providing rich context.\n\n**What you are doing**\nUsing the "Billboard Rule" (leaving out identifiable details), you will ask the AI to help you understand a specific health, legal, or financial situation and follow up until you get a clear answer.`,
          artifact: "Your structured question + the AI's answer.",
          artifactRequired: true,
        },
        {
          id: "s3e3",
          number: 3,
          title: "Check What You Got",
          instructions: `**Goal of this exercise**\nTo identify actionable steps and verify claims.\n\n**What you are doing**\nYou will run the AI's answer through a quality checklist and spend 5 minutes verifying at least one key fact or next step by using a primary, trusted source (like an official government website).`,
          artifact: "Your verified answer + which source you used to cross-check.",
          artifactRequired: true,
        },
      ],
    },
    {
      number: 4,
      slug: "understanding-documents",
      title: "Understanding Documents",
      subtitle: "Turn legal language into plain English, flag what's unusual, and generate questions to ask a professional.",
      outcome: "Turn legal language into plain English, flag what's unusual, and generate questions to ask a professional.",
      exercises: [
        {
          id: "s4e1",
          number: 1,
          title: "Scrub and Summarize",
          instructions: `**Goal of this exercise**\nTo safely use AI to translate dense or confusing documents into plain English.\n\n**What you are doing**\nYou will take a real contract, policy, or document, scrub it of all personal identifiable information, and ask the AI to summarize its meaning.`,
          artifact: "Your scrubbed excerpt + AI's plain-English summary.",
          artifactRequired: true,
        },
        {
          id: "s4e2",
          number: 2,
          title: "Flag It and Question It",
          instructions: `**Goal of this exercise**\nTo find hidden red flags and prepare questions for a professional.\n\n**What you are doing**\nYou will prompt the AI to find anything that is unusual or heavily one-sided in your document, and have it auto-generate three specific questions you can ask your provider before signing.`,
          artifact: "Your three questions (the filled notepad).",
          artifactRequired: true,
        },
        {
          id: "s4e3",
          number: 3,
          title: "Your Document",
          instructions: `**Goal of this exercise**\nTo run the full summarization and questioning workflow independently.\n\n**What you are doing**\nYou will bring a real lease, medical guide, or HOA agreement, scrub it, run the summary prompt, run the flag prompt, and write down three real questions to take away.`,
          artifact: "Scrubbed excerpt + summary + three questions you're ready to ask.",
          artifactRequired: true,
        },
      ],
    },
    {
      number: 5,
      slug: "ai-as-a-thinking-partner",
      title: "AI as a Thinking Partner",
      subtitle: "Use AI to help you think — not to think for you — and leave with one specific action for the next 48 hours.",
      outcome: "Use AI to help you think — not to think for you — and leave with one specific action for the next 48 hours.",
      exercises: [
        {
          id: "s5e1",
          number: 1,
          title: "Turn AI Into a Thinking Partner",
          instructions: `**Goal of this exercise**\nTo switch the AI from behaving like an answer machine to acting as a strategist.\n\n**What you are doing**\nYou will prompt the AI to help you plan an event or make a decision by explicitly asking it to surface tradeoffs and ask YOU questions, rather than just giving you a generic checklist.`,
          artifact: "Conversation transcript + notes on any blind spots AI surfaced.",
          artifactRequired: true,
        },
        {
          id: "s5e2",
          number: 2,
          title: "Push Back on the Plan",
          instructions: `**Goal of this exercise**\nTo challenge the AI's assumptions and refine its suggestions.\n\n**What you are doing**\nYou will push back on the AI's draft by telling it that the plan has too many steps, the wrong assumptions, or is too vague, finally requesting one specific action you can take in the next 48 hours.`,
          artifact: "Revised plan + the one 48-hour action.",
          artifactRequired: true,
        },
        {
          id: "s5e3",
          number: 3,
          title: "Your Real Situation",
          instructions: `**Goal of this exercise**\nTo complete a full strategy session for a real-life situation.\n\n**What you are doing**\nYou will take a real upcoming conversation, event, or project in your life and work through 3 or 4 turns of prompting, ending with a concrete next step.`,
          artifact: "Full conversation transcript + your 48-hour next step.",
          artifactRequired: true,
        },
      ],
    },
    {
      number: 6,
      slug: "analyze-and-get-answers",
      title: "Analyze and Get Answers",
      subtitle: "Prepare real feedback data, run AI through 5 analysis questions, and spot-check the findings.",
      outcome: "Prepare real feedback data, run AI through 5 analysis questions, and spot-check the findings.",
      exercises: [
        {
          id: "s6e1",
          number: 1,
          title: "Prepare Data + First Two Questions",
          instructions: `**Goal of this exercise**\nTo securely set up unstructured data for AI analysis.\n\n**What you are doing**\nYou will anonymize and properly format a list of feedback or reviews, paste them into the AI, and ask it to identify the top themes and overall sentiment.`,
          artifact: "AI's themes + sentiment response.",
          artifactRequired: true,
        },
        {
          id: "s6e2",
          number: 2,
          title: "Spot-Check the Findings",
          instructions: `**Goal of this exercise**\nTo verify that the AI's analysis is grounded in real data, not hallucinated patterns.\n\n**What you are doing**\nYou will ask the AI to provide exact, word-for-word quotes that support its earlier claims, ensuring it didn't invent a trend that doesn't actually exist in the source text.`,
          artifact: "Spot-check results + priority recommendations + 5-sentence executive summary.",
          artifactRequired: true,
        },
        {
          id: "s6e3",
          number: 3,
          title: "Analyze Your Own Feedback",
          instructions: `**Goal of this exercise**\nTo execute a full data analysis cycle on your own information.\n\n**What you are doing**\nYou will bring your own business reviews or survey feedback, run all 5 analysis questions (Themes, Sentiment, Outliers, Priorities, Summary), and spot-check the results.`,
          artifact: "Full 5-question analysis + 2+ spot-checks + final summary.",
          artifactRequired: true,
        },
      ],
    },
    {
      number: 7,
      slug: "visual-creation",
      title: "Visual Creation",
      subtitle: "Create images and presentations that are specific, on-brand, and actually usable.",
      outcome: "Create images and presentations that are specific, on-brand, and actually usable.",
      exercises: [
        {
          id: "s7e1",
          number: 1,
          title: "Describe What You See in Your Head",
          instructions: `**Goal of this exercise**\nTo generate compelling imagery using structured visual prompting.\n\n**What you are doing**\nYou will use the 6-Element Image Framework (Subject, Setting, Mood, Lighting, Style, Format) to generate an image, refining the response 2 to 3 times until it matches your vision.`,
          artifact: "Your final generated image (flyer, card, banner, or your own scenario).",
          artifactRequired: true,
        },
        {
          id: "s7e2",
          number: 2,
          title: "Outline Before You Build",
          instructions: `**Goal of this exercise**\nTo correctly orchestrate the creation of a presentation.\n\n**What you are doing**\nYou will use the AI to generate an outline and structural list of slides first. You will edit and adjust the outline manually before ever asking the AI to build the final content.`,
          artifact: "Your reviewed and adjusted outline.",
          artifactRequired: true,
        },
        {
          id: "s7e3",
          number: 3,
          title: "Make Something You'll Actually Use",
          instructions: `**Goal of this exercise**\nTo put your visual tools into practical use.\n\n**What you are doing**\nYou will create a real flyer, presentation, or audio overview for an upcoming project, refining it repeatedly until it is ready to be exported and used.`,
          artifact: "Your finished visual — flyer / deck / image / audio overview.",
          artifactRequired: true,
        },
      ],
    },
    {
      number: 8,
      slug: "capstone",
      title: "Capstone",
      subtitle: "Pick a real problem, solve it with the full toolkit, and build your personal AI toolkit.",
      outcome: "Pick a real problem, solve it with the full toolkit, and build your personal AI toolkit.",
      exercises: [
        {
          id: "s8e1",
          number: 1,
          title: "Choose Your Problem and Start",
          instructions: `**Goal of this exercise**\nTo tackle a real-world task organically, using the skills you've developed.\n\n**What you are doing**\nYou will choose a real problem you currently face (like disputing a bill, planning a trip, or summarizing a long read) and use the correct prompting frameworks to solve it from start to finish.`,
          artifact: "Your completed solution — letter, plan, summary, analysis, deck, image, whatever you made.",
          artifactRequired: true,
        },
        {
          id: "s8e2",
          number: 2,
          title: "Build Your Personal Toolkit",
          instructions: `**Goal of this exercise**\nTo document your AI boundaries, favorite prompts, and fastest workflows.\n\n**What you are doing**\nYou will create a permanent reference document detailing your top 5 AI use cases, your personal data-privacy rules, and your go-to "Starter Prompt" for new chats.`,
          artifact: "Your personal toolkit (text doc, note, photo of a handwritten card — whatever format works).",
          artifactRequired: true,
        },
        {
          id: "s8e3",
          number: 3,
          title: "Share What You Did",
          instructions: `**Goal of this exercise**\nTo recap strategies with the cohort and discover new applications.\n\n**What you are doing**\nYou will discuss what parts of your workflow succeeded, what broke down, and share one practical way you plan to use AI in the upcoming week.`,
          artifact: "Optional — any reflection notes you want to keep.",
          artifactRequired: false,
        },
      ],
    },
  ],
};
